import {Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {RowStatus} from '../../../../../model/row-status';
import {TranslateService} from '@ngx-translate/core';
import {DataTableFiller} from '../../../../../model/data-table-filler';
import {CheckboxChangeEvent} from 'primeng/checkbox';

/**
 * Display fold workflow list, not using generic foldList because he could easily change
 */
@Component({
  selector: 'app-fold-workflow-list',
  templateUrl: './fold-workflow-list.html',
  styleUrls: ['./fold-workflow-list.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FoldWorkflowListComponent implements OnInit, OnChanges {

  /**
   * fold selected by user (not ticked)
   */
  @Output('selectedFold') public selectedFold: EventEmitter<any>;

  /**
   * List of folds for the p-table
   */
  @Input('listOfFolds') public listOfFolds: any[];

  /**
   * Output for the folds ticked by user
   */
  @Output('tickedFolds') public tickedFolds: EventEmitter<any[]>;

  /**
   * List of columns (self build by component)
   */
  public columns: any[];

  /**
   * here for template access
   */
  public DataTableFiller = DataTableFiller;

  /**
   * List of folds index that the user ticked
   */
  public selectedFoldsIndexArray: any[];

  /**
   * Handle Tick All behaviour
   */
  public everyFoldIsSelected: boolean;

  /**
   * Recipient column label
   */
  private recipientColumnLabel: string;

  /**
   * Author column label
   */
  private authorColumnLabel: string;

  /**
   * Depot date column label
   */
  private depotDateColumnLabel: string;

  /**
   * selected (not ticked) fold
   */
  public tempFoldSelection: any;

  /**
   * constructor that instantiate variables
   * @param {TranslateService} translate - handle component translations
   */
  constructor(private translate: TranslateService) {
    this.selectedFold = new EventEmitter<any>();
    this.tickedFolds = new EventEmitter<any[]>();
    this.columns = [];
    this.selectedFoldsIndexArray = [];
  }

  /**
   * Handle calling Trad and calling columns build
   */
  public ngOnInit(): void {
    this.getTradForComponent();
    this.buildColumns();
    this.listOfFolds = this.DataTableFiller.getEmptyArray(DataTableFiller.NUMBER_OF_ROWS_FOR_WORKFLOW_FOLDS_LIST, 'sDepotDateTime');
  }

  /**
   * Trigger when Input change (list of folds) and in charge of reset component
   */
  public ngOnChanges(): void {

    this.selectedFold.emit(null);
    this.selectedFoldsIndexArray = [];
    this.emitTickedFolds();

    if (this.listOfFolds && this.listOfFolds.length > 0) {
      DataTableFiller.fillAnyArray(this.listOfFolds, DataTableFiller.NUMBER_OF_ROWS_FOR_WORKFLOW_FOLDS_LIST, 'sDepotDateTime');
    } else {
      this.listOfFolds = this.DataTableFiller.getEmptyArray(DataTableFiller.NUMBER_OF_ROWS_FOR_WORKFLOW_FOLDS_LIST, 'sDepotDateTime');
    }

    this.everyFoldIsSelected = false;
  }

  /**
   * build static columns
   */
  public buildColumns(): void {
    this.columns.push({field: 'sDepotDateTime', header: this.depotDateColumnLabel, width: '20%' });
    this.columns.push({field: 'creatuser', header: this.authorColumnLabel, width: '15%' });
    this.columns.push({field: 'sDestinataire', header: this.recipientColumnLabel, width: '*' });
  }

  /**
   * Add a fold into selectedFolds and emit new TicketFolds list
   *
   * @param {number} index - index of ticked fold
   */
  private addFoldToSelection(index: number): void {
    if (this.listOfFolds[index] && !this.listOfFolds[index].notSelectable) {
      this.selectedFoldsIndexArray.push(index);
      this.emitTickedFolds();
      if (this.selectedFoldsIndexArray.length >= 0 && this.selectedFoldsIndexArray.length === this.listOfFolds.filter(x => !x.notSelectable).length) {
        this.everyFoldIsSelected = true;
      }
    }
  }

  /**
   * emit the new list of ticked Folds
   */
  private emitTickedFolds(): void {
    const tickedFolds = [];
    for (const index of this.selectedFoldsIndexArray) {
      tickedFolds.push(this.listOfFolds[index]);
    }

    this.tickedFolds.emit(tickedFolds);
  }

  /**
   * Remove a fold from selectedFolds and emit new TicketFolds list
   *
   * @param {number} index - index of ticked fold
   */
  private removeFoldFromSelection(index: number): void {
    this.selectedFoldsIndexArray = this.selectedFoldsIndexArray.filter(x => index !== x);
    this.emitTickedFolds();
    if (this.selectedFoldsIndexArray.length < this.listOfFolds.filter(x => !x.notSelectable).length) {
      this.everyFoldIsSelected = false;
    }
  }

  /**
   * Determine the correct behaviour for the ticked / unticked checkbox from dataTables
   * @param {boolean} addFold - if the user wants to tick a new fold (else it'll remove)
   * @param {number} index - index of the fold to Add/Remove
   */
  public handleAddOrRemove(addFold: CheckboxChangeEvent, index: number): void {
    if (addFold.checked) {
      this.addFoldToSelection(index);
    } else {
      this.removeFoldFromSelection(index);
    }
  }

  /**
   * Determine the correct behaviour for the ticked / unticked checkbox SelectAll
   * @param {boolean} add - if the user wants to tick every fold
   */
  public handleAddOrRemoveEveryFolds(add: CheckboxChangeEvent) {
    if (add.checked) {
      this.addAllFoldsToSelection();
    } else {
      this.removeAllFoldFromSelection();
    }
  }

  /**
   * Determine if a fold is selected or not
   * @param {number} index - index of the fold inside listOfFolds
   * @return {boolean} - true if the fold is in the list
   */
  public rowIsSelected(index: number): boolean {
    return this.selectedFoldsIndexArray.indexOf(index) >= 0;
  }

  /**
   * Remove every available fold to ticked folds list
   */
  private removeAllFoldFromSelection(): void {
    this.selectedFoldsIndexArray = [];
    this.emitTickedFolds();
  }

  /**
   * Add every available fold to ticked folds list
   */
  private addAllFoldsToSelection(): void {
    for (let i = 0; i < this.listOfFolds.length; i++) {
      this.addFoldToSelection(i);
    }

  }

  /**
   * Handle change of fold selection
   * @param {any} newSelectedFold - emit the new fold selection (can be null for unselect case)
   */
  public emitNewFold(newSelectedFold: any) {
    const newFold = Object.assign({}, newSelectedFold);
    newFold.rawStatus = RowStatus.WAITING;
    this.selectedFold.emit(newFold);
  }

  /**
   * function that will determine if we need to select (or not) a row
   * @param {any} event - event containing two things: the clicked row itself and the browser event
   * the fold itself is usefull on click case
   * the function should handle the tick case (a tick is a special click inside a row)
   */
  public handleSelectionOfRow(event: any) {
    if ( !event.data.notSelectable ) {
      if (this.tempFoldSelection && this.tempFoldSelection === event.data) {
        this.tempFoldSelection = null;
        this.selectedFold.emit(null);
      } else {
        this.tempFoldSelection = event.data;
        this.emitNewFold(event.data);
      }
    }
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('LABEL.GIVEN.BY.BACK.vwDepotList.sDepotDateTime.lbl').subscribe((res: string) => {
      this.depotDateColumnLabel = res;
    });
    this.translate.get('LABEL.GIVEN.BY.BACK.vwTrackingMultiCriteria.userId.lbl').subscribe((res: string) => {
      this.authorColumnLabel = res;
    });
    this.translate.get('LABEL.GIVEN.BY.BACK.vwDepotDocDetail.DestRelLbl.lbl').subscribe((res: string) => {
      this.recipientColumnLabel = res;
    });
  }

  public tableIsFullOfEmpty(): boolean {
    for (const fold of this.listOfFolds) {
      if (!fold.notSelectable) {
        return false;
      }
    }
    return true;
  }
}
